import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { auth, db } from '../../services/firebase';

export const REVIEWABLE_DECISION_RECEIPTS_COLLECTION = 'reviewableDecisionReceipts';

export const RECEIPT_ACTIONS = Object.freeze({
  RESOLVED: 'resolved',
  IGNORED: 'ignored',
  AUTO_APPLIED: 'auto_applied',
});

const normalizeText = (value = '') => String(value || '').trim();

const getCurrentUserId = () => auth.currentUser?.uid || null;

const getRegisteredAllowedActions = (domain = '') => {
  try {
    // Lazy require avoids coupling receipt storage to adapter registration at module load.
    // eslint-disable-next-line global-require
    const { getReviewableItemAdapter } = require('./reviewableItemAdapters');
    const adapter = getReviewableItemAdapter(domain);
    return Array.isArray(adapter.allowedActions) ? adapter.allowedActions : [];
  } catch (error) {
    return null;
  }
};

export const validateCandidateDecisionAction = ({
  candidate = {},
  decisionAction = '',
} = {}) => {
  const action = normalizeText(decisionAction);

  if (!action) {
    throw new Error('Missing review decision action.');
  }

  const registeredActions = getRegisteredAllowedActions(candidate.domain || '');
  const allowedActions = registeredActions || (
    Array.isArray(candidate.allowedActions) ? candidate.allowedActions : []
  );

  if (!allowedActions.includes(action)) {
    throw new Error(`Decision action "${action}" is not allowed for ${candidate.domain || 'this item'}.`);
  }

  return action;
};

export const buildDecisionReceipt = ({
  candidate = {},
  action = RECEIPT_ACTIONS.RESOLVED,
  decisionAction = '',
  target = null,
  audit = {},
  originDeleted = false,
  userId = getCurrentUserId(),
  timestamp = serverTimestamp,
} = {}) => {
  const candidateKey = normalizeText(candidate.key || candidate.candidateKey);
  if (!candidateKey) {
    throw new Error('Missing review candidate key.');
  }

  const cleanDecisionAction = validateCandidateDecisionAction({ candidate, decisionAction });
  const receiptAction = normalizeText(action) || RECEIPT_ACTIONS.RESOLVED;
  const decidedAt = timestamp();
  const decidedBy = userId || null;

  return {
    schemaVersion: candidate.schemaVersion || 1,
    childId: candidate.childId || candidate.origin?.childId || '',
    origin: {
      ...(candidate.origin || {}),
      deleted: Boolean(originDeleted || candidate.origin?.deleted),
    },
    domain: candidate.domain || '',
    candidateKey,
    action: receiptAction,
    decisionAction: cleanDecisionAction,
    target: target || null,
    decidedAt,
    decidedBy,
    createdAt: decidedAt,
    createdBy: decidedBy,
    audit: {
      label: candidate.label || candidate.extracted?.name || '',
      sourceText: candidate.sourceText || candidate.extracted?.sourceText || '',
      confidence: candidate.confidence || '',
      reason: audit.reason || '',
      ...audit,
    },
  };
};

export const writeDecisionReceipt = async ({
  candidate = {},
  action = RECEIPT_ACTIONS.RESOLVED,
  decisionAction = '',
  target = null,
  audit = {},
  originDeleted = false,
  deps = {},
} = {}) => {
  const firestore = deps.db || db;
  const docFn = deps.doc || doc;
  const setDocument = deps.setDoc || setDoc;
  const receipt = buildDecisionReceipt({
    candidate,
    action,
    decisionAction,
    target,
    audit,
    originDeleted,
    userId: deps.userId === undefined ? getCurrentUserId() : deps.userId,
    timestamp: deps.serverTimestamp || serverTimestamp,
  });

  await setDocument(
    docFn(firestore, REVIEWABLE_DECISION_RECEIPTS_COLLECTION, receipt.candidateKey),
    receipt,
    { merge: true }
  );

  return receipt;
};

export const requireDecisionReceiptsForCandidateKeys = async (candidateKeys = [], deps = {}) => {
  const keys = [...new Set((Array.isArray(candidateKeys) ? candidateKeys : [])
    .map(normalizeText)
    .filter(Boolean))];

  if (!keys.length) {
    return [];
  }

  const firestore = deps.db || db;
  const docFn = deps.doc || doc;
  const getDocument = deps.getDoc || getDoc;
  const snapshots = await Promise.all(keys.map((key) => (
    getDocument(docFn(firestore, REVIEWABLE_DECISION_RECEIPTS_COLLECTION, key))
  )));

  return snapshots
    .filter((snapshot) => snapshot?.exists?.())
    .map((snapshot) => ({
      id: snapshot.id,
      ...snapshot.data(),
    }));
};

// Keep existing best-effort readers unchanged; the queue must distinguish a failed
// receipt read from a successful read of a receipt that does not exist.
export const fetchDecisionReceiptsForCandidateKeys = (candidateKeys = [], deps = {}) => {
  const getDocument = deps.getDoc || getDoc;
  return requireDecisionReceiptsForCandidateKeys(candidateKeys, {
    ...deps,
    getDoc: ref => Promise.resolve().then(() => getDocument(ref)).catch(() => null),
  });
};

export const deleteDecisionReceipt = async (candidateKey = '', deps = {}) => {
  const cleanCandidateKey = normalizeText(candidateKey);
  if (!cleanCandidateKey) {
    throw new Error('Missing review candidate key.');
  }

  const firestore = deps.db || db;
  const docFn = deps.doc || doc;
  const deleteDocument = deps.deleteDoc || deleteDoc;
  await deleteDocument(docFn(
    firestore,
    REVIEWABLE_DECISION_RECEIPTS_COLLECTION,
    cleanCandidateKey
  ));
};

export const markDecisionReceiptsOriginDeleted = async ({
  originId = '',
  originType = '',
  originCollection = '',
  deps = {},
} = {}) => {
  const cleanOriginId = String(originId || '').trim();
  if (!cleanOriginId) {
    throw new Error('Missing receipt origin id.');
  }

  const firestore = deps.db || db;
  const collectionFn = deps.collection || collection;
  const getDocuments = deps.getDocs || getDocs;
  const queryFn = deps.query || query;
  const updateDocument = deps.updateDoc || updateDoc;
  const whereFn = deps.where || where;
  const timestamp = deps.serverTimestamp || serverTimestamp;
  const receiptCollection = collectionFn(firestore, REVIEWABLE_DECISION_RECEIPTS_COLLECTION);
  const constraints = [
    whereFn('origin.id', '==', cleanOriginId),
  ];

  if (originType) {
    constraints.push(whereFn('origin.type', '==', String(originType || '').trim()));
  }
  if (originCollection) {
    constraints.push(whereFn('origin.collection', '==', String(originCollection || '').trim()));
  }

  const snapshot = await getDocuments(queryFn(receiptCollection, ...constraints));
  const receiptDocs = Array.isArray(snapshot?.docs) ? snapshot.docs : [];

  await Promise.all(receiptDocs.map((receiptDoc) => updateDocument(receiptDoc.ref || receiptDoc, {
    'origin.deleted': true,
    'origin.deletedAt': timestamp(),
    'origin.deletedBy': deps.userId === undefined ? getCurrentUserId() : deps.userId,
  })));

  return receiptDocs.length;
};
